import type { PermitCountByCategory } from '@contracts/order/OrderListResponse';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Error from '@mui/icons-material/Error';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Skeleton from '@mui/material/Skeleton';
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Drawer } from '@src/common/components/Drawer';
import { FilterableTable } from '@src/common/components/FilterableTable';
import { Stack } from '@src/common/components/Stack';
import { Tooltip } from '@src/common/components/Tooltip';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import type { User } from '@src/manageUsers/domain/User';
import { useUsers } from '@src/manageUsers/hooks/useUsers';
import { OrderStatusBadge } from '@src/orders/components/OrderStatusBadge';
import { useCurrentPermit } from '@src/orders/components/permits/useCurrentPermit';
import { CountBadge } from '@src/orders/CountBadge';
import type { OrderListing } from '@src/orders/domain/OrderList';
import type { OrderTab as Tab } from '@src/orders/domain/OrderTab';
import { useCurrentOrder } from '@src/orders/hooks/useCurrentOrder';
import { useCurrentOrderMode } from '@src/orders/hooks/useCurrentOrderMode';
import { useCurrentPage } from '@src/orders/hooks/useCurrentPage';
import { useDebounce } from '@src/orders/hooks/useDebounce';
import { useOrderFiltersOptions } from '@src/orders/hooks/useOrderFiltersOptions';
import { useOrderListing } from '@src/orders/hooks/useOrderListing';
import type { SortBy } from '@src/orders/hooks/useOrdersWithFilters';
import { useOrdersPermitGroupsCountByCategory } from '@src/orders/hooks/useOrdersWithFilters';
import { useOrdersWithFilters } from '@src/orders/hooks/useOrdersWithFilters';
import { OrderDrawerContent } from '@src/orders/OrderDrawerContent';
import { PermitNotes } from '@src/permitNote/PermitNotes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const OrderTable = ({
  tab,
  assignedToMe,
  onOrderChange,
}: {
  tab: Tab;
  assignedToMe: boolean;
  onOrderChange: (orderId: string) => void;
}) => {
  const { t } = useTranslation();
  const { userList } = useUsers();
  const { currentPage, setCurrentPage, pageSize, setPageSize } =
    useCurrentPage();

  const { mode } = useCurrentOrderMode();

  const [searchValue, setSearchValue] = useState('');
  const searchValueDebounced = useDebounce(searchValue, 500);

  const [sortBy, setSortBy] = useState<SortBy | undefined>();
  const [permitCountsData, setPermitCountsData] = useState<
    Record<string, PermitCountByCategory>
  >({});

  const [displayData, setDisplayData] = useState<{
    orders: OrderListing[];
    totalOrders: number;
  }>({ orders: [], totalOrders: 0 });

  const { filters, selectFilters, selectedFilters } =
    useOrderFiltersOptions(tab);

  const {
    orders: { orders, totalOrders },
    isLoading,
  } = useOrdersWithFilters({
    assignedToMe,
    category: tab,
    page: currentPage + 1,
    pageSize,
    searchValue: searchValueDebounced || undefined,
    sortBy,
    status: selectedFilters.status,
    clientId: selectedFilters.client,
    assigneeId: selectedFilters.assignee,
  });

  useEffect(() => {
    if (!isLoading && orders.length >= 0) {
      setDisplayData({ orders, totalOrders });
    }
  }, [orders, totalOrders, isLoading]);

  const { createOrder: fetchPermitCounts, isLoading: isLoadingPermitCounts } =
    useOrdersPermitGroupsCountByCategory({
      onSuccess: (data) => {
        const permitCountsMap = data.reduce(
          (acc, item) => {
            acc[item.orderId] = item;
            return acc;
          },
          {} as Record<string, PermitCountByCategory>,
        );
        setPermitCountsData(permitCountsMap);
      },
    });

  useEffect(() => {
    if (displayData.orders.length > 0) {
      const orderIds = displayData.orders.map((order) => order.id);
      fetchPermitCounts({ orderIds });
    }
  }, [displayData.orders, fetchPermitCounts]);

  const { setCurrentOrder, removeCurrentOrder, orderId } = useCurrentOrder();

  const changePage = ({ pageSize, page }: GridPaginationModel) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const showOrder = (order: OrderListing) => {
    setCurrentOrder(order.id);
  };

  const openOrderDetails = (params: GridRowParams) => {
    const order = displayData.orders.find((order) => order.id === params.id);
    if (!order) {
      return;
    }
    setCurrentOrder(order.id);
  };

  const isDrawerOpen = !!orderId;
  const closeDrawer = () => removeCurrentOrder();

  const usersById: Record<string, User> = userList.users.reduce(
    (accumulator, user) => {
      return { ...accumulator, [user.id]: user };
    },
    {},
  );

  const getOrderAssignee = useCallback(
    (order: OrderListing) => {
      return order.claimedBy ? (usersById[order.claimedBy]?.name ?? '') : '';
    },
    [usersById],
  );

  const { user } = useAuthentication();

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: t('orders.status'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <OrderStatusBadge status={params.value} />
      ),
    },
    {
      field: 'displayId',
      headerName: t('orders.number'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        OrderNumberCell(params.value, params.row.isInError),
    },
    {
      field: 'client',
      headerName: t('orders.client'),
      flex: 1,
      renderCell: ClientCell,
    },
    {
      field: 'tractorUnit',
      headerName: t('orders.tractorUnit'),
      flex: 0.75,
      renderCell: OrderDetailsCell,
      sortable: false,
    },
    {
      field: 'tractorLicensePlate',
      headerName: t('orders.tractorLicensePlate'),
      flex: 0.75,
      renderCell: OrderDetailsCell,
      sortable: false,
    },
    {
      field: 'leadTrailerUnit',
      headerName: t('orders.trailerUnit'),
      flex: 0.75,
      renderCell: OrderDetailsCell,
      sortable: false,
    },
    {
      field: 'leadTrailerLicensePlate',
      headerName: t('orders.trailerLicensePlate'),
      flex: 0.75,
      renderCell: OrderDetailsCell,
      sortable: false,
    },
    {
      field: 'PONumber',
      headerName: t('orders.poNumber'),
      flex: 0.75,
      renderCell: OrderDetailsCell,
    },
    {
      field: 'assignee',
      headerName: isClientUser(user) ? t('orders.agent') : t('orders.assignee'),
      flex: 1,
      renderCell: AssigneeCell,
    },
    {
      field: 'effectiveDate',
      headerName: t('orders.effectiveDate'),
      flex: 1,
      renderCell: OrderDetailsCell,
    },
    {
      field: 'permitCounts',
      headerName: t('orders.permits'),
      flex: 0.75,
      renderCell: (params) => (
        <PermitsCell
          {...params}
          isLoading={isLoadingPermitCounts && !permitCountsData[params.row.id]}
        />
      ),
      renderHeader: (params) => (
        <Stack direction="row" alignItems="center" gap={1}>
          {params.colDef.headerName}
          <Tooltip
            title={
              <Stack spacing={1} sx={{ p: 0.5 }}>
                <Typography variant="subtitle2" component="div">
                  {t('orders.permitsInfo.title')}
                </Typography>
                <Typography variant="body2" component="div">
                  {t('orders.permitsInfo.description')}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CountBadge count={1} danger />
                  <Typography variant="body2">
                    {t('orders.permitsInfo.priority')}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CountBadge count={1} />
                  <Typography variant="body2">
                    {t('orders.permitsInfo.regular')}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CountBadge count={1} contained />
                  <Typography variant="body2">
                    {t('orders.permitsInfo.awaiting')}
                  </Typography>
                </Stack>
              </Stack>
            }
            placement="top"
            isClear={true}
          >
            <InfoOutlined
              fontSize="small"
              sx={{
                color: 'action.active',
                width: '16px',
                height: '16px',
              }}
            />
          </Tooltip>
        </Stack>
      ),
      sortable: false,
    },
  ];

  const rows = useMemo(
    () =>
      displayData.orders.map((order) => {
        return {
          id: order.id,
          status: order.status,
          displayId: order.displayId,
          client: order.client.doingBusinessAs
            ? order.client.doingBusinessAs
            : order.client.name,
          PONumber: order.details.PONumber,
          tractorUnit: order.details.tractorUnit,
          tractorLicensePlate: order.details.tractorLicensePlate,
          leadTrailerUnit: order.details.leadTrailerUnit,
          leadTrailerLicensePlate: order.details.leadTrailerLicensePlate,
          assignee: getOrderAssignee(order),
          effectiveDate:
            order.details.effectiveDate.formatUTCDate('dd/MM/yyyy HH:mm'),
          permitCounts: permitCountsData[order.id] || order.permitCounts,
          clientSubtext: order.client.doingBusinessAs ? order.client.name : '',
          isInError: order.requiresAttention,
        };
      }),
    [displayData.orders, getOrderAssignee, permitCountsData],
  );

  const shouldShowLoading = isLoading && displayData.orders.length === 0;

  return (
    <>
      <FilterableTable
        columns={columns}
        rows={rows}
        totalRowCount={displayData.totalOrders}
        onRowClick={openOrderDetails}
        pageSize={pageSize}
        isLoading={shouldShowLoading}
        currentPage={currentPage}
        onCurrentPageChange={changePage}
        paginationAndSortMode="server"
        ariaLabel="orders-table"
        onSearchValue={(value) => setSearchValue(value.trim())}
        onSort={setSortBy}
        filters={{
          filters,
          onChange: selectFilters,
        }}
      />
      {orderId && (
        <OrderDetailsDrawer
          isDrawerOpen={isDrawerOpen}
          orderId={orderId}
          closeDrawer={closeDrawer}
          onOrderChange={onOrderChange}
          mode={mode}
          showOrder={showOrder}
        />
      )}
    </>
  );
};

const OrderDetailsDrawer = ({
  orderId,
  isDrawerOpen,
  closeDrawer,
  mode,
  onOrderChange,
  showOrder,
}: {
  orderId: string;
  isDrawerOpen: boolean;
  closeDrawer: () => void;
  onOrderChange: (orderId: string) => void;
  mode: string;
  showOrder: (orderListing: OrderListing) => void;
}) => {
  const { orderListing: currentOrder } = useOrderListing({ orderId });

  const { permitId: currentPermitId } = useCurrentPermit();
  const currentPermit = currentPermitId
    ? currentOrder?.getPermit(currentPermitId)
    : undefined;

  return (
    <Drawer open={isDrawerOpen} onClose={closeDrawer}>
      {currentOrder && currentPermit && mode === 'permitNotes' && (
        <PermitNotes permitSummary={currentPermit} mode="permitNotes" />
      )}

      {currentOrder && currentPermit && mode === 'reviewPermitNotes' && (
        <PermitNotes permitSummary={currentPermit} mode="reviewPermitNotes" />
      )}

      {!!currentOrder &&
        (mode === 'summary' || mode === 'review' || mode === 'viewInvoice') && (
          <OrderDrawerContent
            currentOrder={currentOrder}
            mode={mode}
            onDeleteSuccess={closeDrawer}
            onOrderChange={() => onOrderChange(currentOrder.id)}
            onPermitChange={showOrder}
          />
        )}
    </Drawer>
  );
};

const OrderNumberCell = (displayId: string, isInError: boolean = false) => (
  <Stack
    height="100%"
    alignItems="center"
    direction="row"
    gap={theme.spacing['2']}
  >
    {isInError && <Error data-testid="error-icon" className={error} />}
    <Typography
      className={isInError ? error : undefined}
      style={theme.typography.label.large}
    >{`#${displayId}`}</Typography>
  </Stack>
);

const ClientCell = (params: GridRenderCellParams) => (
  <Stack height="100%" justifyContent="center">
    <Stack>
      <Typography style={theme.typography.body.large}>
        {params.value}
      </Typography>
      {params.row.clientSubtext && (
        <Typography
          style={{
            ...theme.typography.body.small,
            color: theme.palette.surface.onSurfaceVariant,
          }}
        >
          {params.row.clientSubtext}
        </Typography>
      )}
    </Stack>
  </Stack>
);

const AssigneeCell = (params: GridRenderCellParams) => {
  const { t } = useTranslation();
  return (
    <Stack height="100%" alignItems="center" gap={1} flexDirection="row">
      <AccountCircle />
      {params.value === '' ? (
        <Typography
          style={{
            ...theme.typography.body.large,
            color: theme.palette.surface.onSurfaceVariant,
          }}
        >
          {t('unassigned')}
        </Typography>
      ) : (
        <Typography style={theme.typography.body.large}>
          {params.value}
        </Typography>
      )}
    </Stack>
  );
};

const OrderDetailsCell = (params: GridRenderCellParams) => (
  <Stack height="100%" justifyContent="center">
    <Typography style={theme.typography.body.large}>{params.value}</Typography>
  </Stack>
);

const PermitsCell = ({
  value,
  isLoading = false,
}: GridRenderCellParams<GridValidRowModel, PermitCountByCategory> & {
  isLoading: boolean;
}) => {
  return (
    <Stack
      height="100%"
      direction="row"
      alignItems="center"
      gap={theme.spacing['2']}
      data-testid="permits-cell"
    >
      {isLoading
        ? Array.from({ length: 3 }).map((_, skeletonKey) => (
            <Skeleton
              key={skeletonKey}
              variant="rounded"
              width={32}
              height={20}
              className={skeletonBadge}
              data-testid="skeleton"
            />
          ))
        : [
            !!value?.priority && (
              <CountBadge key="priority" count={value.priority} danger />
            ),
            !!value?.regular && (
              <CountBadge key="regular" count={value.regular} />
            ),
            !!value?.awaiting && (
              <CountBadge key="awaiting" count={value.awaiting} contained />
            ),
          ]}
    </Stack>
  );
};

const error = css(() => ({
  color: colors.error['40'],
}));

const skeletonBadge = css(() => ({
  borderRadius: theme.spacing['1'],
}));
